/**
 * End Customer Analytics API service
 *
 * Provides cost analytics data for the End Customer including:
 * - Cost summary and trends
 * - Resource cost distribution
 * - Billing history
 * - Per-VM cost ranking
 */

import { apiClient } from './api'

// --- Types ---

export type AnalyticsSummary = {
  total_monthly_cost: number
  total_vms: number
  active_vms: number
  billable_vms: number
  idle_vms: number
  idle_cost: number
  avg_cost_per_vm: number
  cost_trend_percent: number
}

export type ResourceCostItem = {
  total: number
  cost_estimate: number
}

export type MemoryResourceCostItem = {
  total_gb: number
  cost_estimate: number
}

export type StorageResourceCostItem = {
  total_gb: number
  cost_estimate: number
}

export type CostByResource = {
  cpu: ResourceCostItem
  memory: MemoryResourceCostItem
  storage: StorageResourceCostItem
  estimated: boolean
}

export type BillingHistoryPoint = {
  month: string
  month_label: string
  amount: number
}

export type VMCostItem = {
  id: string
  name: string
  status: string
  cpu: number
  ram_gb: number
  storage_gb: number
  monthly_cost: number
  percentage_of_total: number
  billing_cycle: string
  is_idle: boolean
  is_billable: boolean
  billing_status: string
}

export type CustomerAnalyticsResponse = {
  summary: AnalyticsSummary
  cost_by_resource: CostByResource
  billing_history: BillingHistoryPoint[]
  vm_costs: VMCostItem[]
  timestamp: string
}

// --- API Functions ---

/**
 * Get cost analytics data for end customer
 */
export const getCustomerAnalytics = async (): Promise<CustomerAnalyticsResponse> => {
  return apiClient.get('/api/v1/end-customer/analytics')
}

// --- Helper Functions ---

/**
 * Format currency value for analytics display
 */
export const formatAnalyticsCurrency = (value: number, currency: string = 'BRL'): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value)
}

/**
 * Get status color classes for VM status
 */
export const getVMStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    running: 'bg-green-100 text-green-800',
    stopped: 'bg-red-100 text-red-800',
    creating: 'bg-yellow-100 text-yellow-800',
    starting: 'bg-yellow-100 text-yellow-800',
    stopping: 'bg-orange-100 text-orange-800',
    error: 'bg-red-100 text-red-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Get status label in Portuguese
 */
export const getVMStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    running: 'Online',
    stopped: 'Parada',
    creating: 'Criando',
    starting: 'Iniciando',
    stopping: 'Parando',
    error: 'Erro',
  }
  return labels[status] || status
}

// --- Export ---

const endCustomerAnalyticsApi = {
  getCustomerAnalytics,
  formatAnalyticsCurrency,
  getVMStatusColor,
  getVMStatusLabel,
}

export default endCustomerAnalyticsApi
